'use client';

import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * shadcn Command (cmdk), restyled to the Figma Combobox List (91:11196) and
 * Combobox Option (91:11106). cmdk gives the listbox / option roles, the
 * active option (`data-selected="true"`, aria-selected), pointer
 * highlighting, arrow / Home / End / Enter keys on the root and scrolling
 * the active option into view. The public component is `Combobox`
 * (components/lib), which brings its own combobox input: cmdk's
 * `Command.Input` hard-codes `id`, `aria-expanded` and `aria-labelledby`,
 * which breaks Field labels and the closed state, so it is not vendored here;
 * nor is `Command.Separator` - a listbox may only hold options.
 */
function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return <CommandPrimitive data-slot="command" className={className} {...props} />;
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('scroll-py-2 outline-none', className)}
      {...props}
    />
  );
}

/** Combobox Option: 40 tall, radius/sm, 8px sides, gap 8; Active = bg/muted. */
function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        'relative flex h-10 w-full cursor-default items-center gap-2 rounded-sm px-2 outline-none select-none',
        'text-body-md text-text-primary',
        'data-[selected=true]:bg-bg-muted',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:text-text-disabled',
        className,
      )}
      {...props}
    />
  );
}

/**
 * A titled group of options (Command Palette sections). cmdk renders the
 * heading `aria-hidden` and names the `role="group"` with it - a listbox may
 * hold groups of options.
 */
function CommandGroup(props: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return <CommandPrimitive.Group data-slot="command-group" {...props} />;
}

export { Command, CommandGroup, CommandItem, CommandList };
